import type {
  DealThesisSourceRef,
  GovernedDependencyRef,
  GovernedEntityOrCapabilityRef,
  GovernedRef,
} from './ma-deal-thesis';

/** Explicit authored relation; the target is resolved in the selected run's org state. */
export type DealThesisOrgExposureBinding = Readonly<{
  sourceRef: GovernedDependencyRef | GovernedEntityOrCapabilityRef;
  targetRef: Pick<GovernedRef<'capability' | 'position'>, 'refKind' | 'id' | 'tenantRef'>;
  sourceRefs: readonly DealThesisSourceRef[];
}>;

export function validateOrgExposureBindings(
  outcome: Record<string, unknown>,
  errors: string[]
): void {
  if (outcome.orgExposureBindings === undefined) return;
  if (!Array.isArray(outcome.orgExposureBindings)) {
    errors.push('orgExposureBindings must be an array');
    return;
  }
  const refs = [outcome.dependencyRefs, outcome.criticalAssetRefs].flatMap((value) =>
    Array.isArray(value) ? value.filter(isRecord) : []
  );
  const seen = new Set<number>();
  for (const [index, binding] of outcome.orgExposureBindings.entries()) {
    const path = `orgExposureBindings[${index}]`;
    if (!isRecord(binding)) {
      errors.push(`${path} must be an object`);
      continue;
    }
    if (Object.keys(binding).some((key) => !['sourceRef', 'targetRef', 'sourceRefs'].includes(key)))
      errors.push(`${path} contains an unsupported field`);
    const sourceIndex = refs.findIndex((ref) => thesisReferencesMatch(ref, binding.sourceRef));
    if (sourceIndex < 0 || seen.has(sourceIndex))
      errors.push(
        `${path}.sourceRef must uniquely match a dependency or critical asset on this outcome`
      );
    seen.add(sourceIndex);
    validateExposureTarget(binding.targetRef, `${path}.targetRef`, errors);
    if (!Array.isArray(binding.sourceRefs) || !binding.sourceRefs.length)
      errors.push(`${path}.sourceRefs must include authored provenance`);
    else
      binding.sourceRefs.forEach((ref, sourceIndex) =>
        validateRef(ref, `${path}.sourceRefs[${sourceIndex}]`, 'governed_source', errors)
      );
  }
}

export function thesisReferencesMatch(left: Record<string, unknown>, right: unknown): boolean {
  return (
    isRecord(right) &&
    Object.keys(left).length === Object.keys(right).length &&
    Object.entries(left).every(([key, value]) => right[key] === value)
  );
}

function validateExposureTarget(value: unknown, path: string, errors: string[]): void {
  if (!isRecord(value) || (value.refKind !== 'capability' && value.refKind !== 'position')) {
    errors.push(`${path} must name an explicit capability or position`);
    return;
  }
  validateRef(value, path, value.refKind, errors);
  if (Object.keys(value).some((key) => !['refKind', 'id', 'tenantRef'].includes(key)))
    errors.push(`${path} contains an unsupported identity field`);
  if (value.tenantRef !== undefined) requireNonBlank(value.tenantRef, `${path}.tenantRef`, errors);
}

export function validateRef(value: unknown, path: string, refKind: string, errors: string[]): void {
  if (!isRecord(value)) {
    errors.push(`${path} is required`);
    return;
  }

  if (value.refKind !== refKind) {
    errors.push(`${path}.refKind must be ${refKind}`);
  }
  requireNonBlank(value.id, `${path}.id`, errors);
  if (value.version !== undefined && !isPositiveInteger(value.version)) {
    errors.push(`${path}.version must be a positive integer when provided`);
  }
  if (value.effectiveAt !== undefined && !isNonBlankString(value.effectiveAt)) {
    errors.push(`${path}.effectiveAt must be a non-empty string when provided`);
  }
}

export function requireNonBlank(value: unknown, path: string, errors: string[]): void {
  if (!isNonBlankString(value)) {
    errors.push(`${path} is required`);
  }
}

export function isPositiveInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value > 0;
}

export function isNonBlankString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
